/**
 * Список вариантов для choice/poll — общий рендерер для ChoiceView и PollView.
 *
 * `variant="answer"`: варианты — кнопки; клик → onPick(i) и блокировка.
 * `variant="spectate"`: варианты — карточки только для чтения + живая
 * статистика (counts) барами. Подсветка верного/ошибочного включается флагом
 * `showCorrectMark` (choice — да, poll — нет, т.к. у опроса нет правильного).
 *
 * Вынесен отдельно, чтобы ChoiceView и PollView не дублировали разметку.
 */
import { useTranslation } from 'react-i18next';
import { optionLabel } from '../ui-helpers';
import { FormulaText } from '../FormulaText';

export interface OptionsBoardProps {
  options: string[];
  variant: 'answer' | 'spectate';
  /** Текущий выбор игрока (для подсветки); null — ничего не выбрано. */
  selected: number | null;
  /** Заблокирован ли ввод (после ответа/раскрытия). */
  locked: boolean;
  /** Индекс верного варианта (reveal) либо null. */
  correctOption: number | null;
  /** Живая статистика counts[i] (spectate); null — скрыть бары. */
  counts: number[] | null;
  /** Подсвечивать верный/ошибочный (choice — true; poll — false). */
  showCorrectMark: boolean;
  onPick: (i: number) => void;
}

export function OptionsBoard({
  options,
  variant,
  selected,
  locked,
  correctOption,
  counts,
  showCorrectMark,
  onPick,
}: OptionsBoardProps): JSX.Element {
  const { t } = useTranslation();
  const revealed = correctOption !== null;
  const totalVotes = counts ? counts.reduce((a, b) => a + b, 0) : 0;

  function optionClass(i: number): string {
    const chosen = selected === i;
    const isCorrect = showCorrectMark && revealed && correctOption === i;
    const isWrongChosen = showCorrectMark && revealed && chosen && !isCorrect;
    const classes = ['option'];
    if (isCorrect) classes.push('option--correct');
    if (isWrongChosen) classes.push('option--wrong');
    if (chosen && !revealed) classes.push('option--chosen');
    if (locked) classes.push('option--disabled');
    return classes.join(' ');
  }

  return (
    <div className="options">
      {options.map((option, i) => {
        const count = counts?.[i] ?? 0;
        const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
        const isCorrect = showCorrectMark && revealed && correctOption === i;
        const isWrongChosen = showCorrectMark && revealed && selected === i && !isCorrect;

        if (variant === 'spectate') {
          return (
            <div key={i} className={`${optionClass(i)} option--spectate`} role="listitem">
              <span className="option-index">{optionLabel(i)}</span>
              <FormulaText className="option-text" text={option} />
              {counts ? (
                <span className="option-stat">
                  <span className="stat-bar" aria-hidden="true">
                    <span className="stat-bar-fill" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="option-count">{count}</span>
                </span>
              ) : null}
              {isCorrect ? <span className="option-mark">{t('options.correctMark')}</span> : null}
            </div>
          );
        }

        return (
          <button
            key={i}
            type="button"
            className={optionClass(i)}
            disabled={locked}
            onClick={() => onPick(i)}
            aria-pressed={selected === i}
            aria-label={t('options.optionAria', { label: optionLabel(i), text: option })}
          >
            <span className="option-index" aria-hidden="true">
              {optionLabel(i)}
            </span>
            <FormulaText className="option-text" text={option} />
            {isCorrect ? <span className="option-mark">{t('options.correctMark')}</span> : null}
            {isWrongChosen ? <span className="option-mark">{t('options.yourAnswer')}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
